import { Brand, createBranded } from '@deltasierra/type-utilities';
import { IntegrationConnectivityStatus } from '@deltasierra/features-integration-connections-core';
import { Agency } from '../agency/Agency';
import { Client } from '../client/Client';
import { FullAuditable, HasId, OwnableIntegrationResource } from '../common';
import { Location } from '../location/Location';

export interface AccessToken extends HasId<AccessTokenId>, FullAuditable, OwnableIntegrationResource {
    token: string;
    expiresAt: Date | null;
    refreshToken: string | null;
    tokenSecret: string | null;
    serviceAccountId: string | null;
    serviceAccountName: string | null;
    permissions: string[];
    agency?: Agency;
    client?: Client;
    location?: Location;
    connectivityStatus: IntegrationConnectivityStatus | null;
    connectivityStatusUpdatedAt: Date | null;
}

export type AccessTokenId = Brand<number, 'AccessTokenId'>;
export const AccessTokenId = createBranded<AccessTokenId>();

/**
 * Generate a unique hash for a given access token. This is a great way of comparing if two tokens are similar. Refer
 * to the areAccessTokensSimilar for an explanation of similar tokens.
 *
 * @param token - The access token to hash
 * @returns A string hash of the access token
 * @see areAccessTokensSimilar
 */
export function hashAccessToken(token: AccessToken): string {
    return `${token.service}::${token.serviceAccountId}::${token.token}`;
}

/**
 * Check whether two access token are *similar*. Note that this doesn't check if they're the same. Similar access
 * tokens are those which share the same OAuth values and can be used interchangeably when connecting to a third-party
 * service or API.
 *
 * @param token1 - The the token to compare
 * @param token2 - The other token to compare
 * @returns Whether or not the tokens can be considered similar.
 */
export function areAccessTokensSimilar(token1: AccessToken, token2: AccessToken): boolean {
    return hashAccessToken(token1) === hashAccessToken(token2);
}
